import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { useToast } from "@/hooks/use-toast";
import { CheckCircle } from "lucide-react";
import { z } from "zod";
import { getRecaptchaToken } from "@/hooks/useRecaptcha";

const requestSchema = z.object({
  name: z.string().trim().min(1, "Full name is required").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  phone: z.string().trim().max(20).optional().or(z.literal("")),
  requestType: z.string().min(1, "Please select a request type"),
  message: z.string().trim().max(1000).optional().or(z.literal("")),
});

const REQUEST_TYPES = [
  "Access My Data",
  "Correct My Data",
  "Delete My Data",
  "Opt Out of Targeted Advertising",
  "Data Portability",
];

export default function DataRequest() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    requestType: "",
    message: "",
  });
  const [honeypot, setHoneypot] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    const result = requestSchema.safeParse(form);
    if (!result.success) {
      toast({ title: "Validation Error", description: result.error.issues[0].message, variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const recaptchaToken = await getRecaptchaToken("submit_privacy_request");
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke("submit-privacy-request", {
        body: { ...result.data, recaptchaToken },
      });
      if (error) throw error;
      setSubmitted(true);
    } catch {
      toast({ title: "Error", description: "Something went wrong. Please try again or call or text (973) 725-2395.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Privacy Rights Request | Masonry Pro</title>
        <meta name="description" content="Submit a privacy rights request to Masonry Pro Construction. Access, correct, or delete your personal data under applicable New Jersey privacy laws today." />
        <meta property="og:description" content="Submit a privacy rights request to Masonry Pro Construction. Access, correct, or delete your personal data under applicable New Jersey privacy laws today." />
        <meta property="og:title" content="Privacy Rights Request | Masonry Pro" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        <main className="py-16 md:py-20">
          <div className="container mx-auto px-4 max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">New Jersey Privacy Rights Request</h1>
            <p className="text-muted-foreground mb-10">
              Under the New Jersey Data Privacy Act, you have the right to access, correct, delete, or opt out of the processing of your personal data. Use this form to submit your request. We will respond within 45 days as required by law.
            </p>

            {submitted ? (
              <div className="bg-accent/30 border border-secondary/30 rounded-lg p-8 text-center space-y-4">
                <CheckCircle className="h-12 w-12 text-secondary mx-auto" />
                <h2 className="text-2xl font-bold">Request Received</h2>
                <p className="text-muted-foreground">
                  Your request has been received. We will respond within 45 days as required by New Jersey law.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot */}
                <div className="absolute opacity-0 pointer-events-none" aria-hidden="true" tabIndex={-1}>
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    autoComplete="off"
                    tabIndex={-1}
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="name" className="sr-only">Full Name</label>
                  <Input
                    id="name"
                    placeholder="Full Name *"
                    className="h-12"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    maxLength={100}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="sr-only">Email Address</label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Email Address *"
                    className="h-12"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    maxLength={255}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="sr-only">Phone Number</label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Phone Number (optional)"
                    className="h-12"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    maxLength={20}
                  />
                </div>
                <div>
                  <label htmlFor="requestType" className="sr-only">Request Type</label>
                  <Select value={form.requestType} onValueChange={(val) => setForm({ ...form, requestType: val })}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select Request Type *" />
                    </SelectTrigger>
                    <SelectContent>
                      {REQUEST_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label htmlFor="message" className="sr-only">Message / Details</label>
                  <Textarea
                    id="message"
                    placeholder="Message / Details (optional)"
                    className="min-h-[120px]"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    maxLength={1000}
                  />
                </div>
                <Button type="submit" variant="gold" size="xl" className="w-full" disabled={loading}>
                  {loading ? "SUBMITTING..." : "SUBMIT REQUEST"}
                </Button>
                <p className="text-xs text-muted-foreground/60 text-center leading-snug">
                  This site is protected by reCAPTCHA and the Google{" "}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">Privacy Policy</a> and{" "}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">Terms of Service</a> apply.
                </p>
              </form>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}
